import { useMemberDemographics } from '../hooks/useMemberDemographics.js';
import { useTranslation } from 'react-i18next';
import type {
  PlanDistribution,
  PrefectureDistribution,
  LtvBucket,
  PlanTrend,
  PlanTransition,
  KpiSummary,
} from '../types/member-demographics.js';

/**
 * @graph-node {Page}
 * @graph-stack ac-mens-hub
 * @graph-domain Marketing
 * @graph-business 会員デモグラフィクス分析ページ。サブスク会員の多面的分析（プラン構成比・継続期間・年代・地域・利用回数・LTV・プラン遷移）をダッシュボード形式で表示する。
 * @graph-connects useMemberDemographics [calls] 会員デモグラフィクスデータ取得
 */
export function MemberDemographicsPage() {
  const { t } = useTranslation();
  const { data, isLoading, error } = useMemberDemographics();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="loading-spinner" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="mx-auto max-w-[1280px] p-6">
        <div className="py-12 text-center text-sm text-[var(--color-error)]">
          {t('common.fetchError')}
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1280px] p-6">
      <div className="mb-5 flex items-baseline gap-3">
        <h1 className="text-xl font-semibold text-[var(--color-text-primary)]">
          {t('demographics.title')}
        </h1>
        <span className="rounded bg-[var(--color-bg-elevated)] px-2 py-0.5 text-[11px] text-[var(--color-text-muted)]">
          {t('common.dataUpdate')}: {formatJst(data.snapshotAt)}
        </span>
      </div>

      <KpiCards kpi={data.kpiSummary} />

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <ChartCard title={t('demographics.planRatio')}>
          <PlanDistChart data={data.planDistribution} />
        </ChartCard>
        <ChartCard title={t('demographics.planTrend')}>
          <PlanTrendTable data={data.planTrends} />
        </ChartCard>
        <ChartCard title={t('demographics.tenureDistribution')}>
          <HorizontalBarChart
            data={data.tenureDistribution.map((b) => ({
              label: b.label,
              count: b.count,
              ratio: b.ratio,
            }))}
          />
        </ChartCard>
        <ChartCard title={t('demographics.ageDistribution')}>
          <HorizontalBarChart
            data={data.ageDistribution.map((b) => ({
              label: b.label,
              count: b.count,
              ratio: b.ratio,
            }))}
          />
        </ChartCard>
        <ChartCard title={t('demographics.usageCountDistribution')}>
          <HorizontalBarChart
            data={data.usageCountDistribution.map((b) => ({
              label: b.label,
              count: b.count,
              ratio: b.ratio,
            }))}
          />
        </ChartCard>
        <ChartCard title={t('demographics.ltvDistribution')}>
          <LtvChart data={data.ltvDistribution} />
        </ChartCard>
        <ChartCard title={t('demographics.prefectureDistribution')}>
          <PrefectureChart data={data.prefectureDistribution} />
        </ChartCard>
        <ChartCard title={t('demographics.planTransition')}>
          <PlanTransitionTable data={data.planTransitions} />
        </ChartCard>
      </div>
    </div>
  );
}

/** @graph-connects none */
function KpiCards({ kpi }: { kpi: KpiSummary }) {
  const { t } = useTranslation();
  /** @graph-connects none */
  const cards = [
    {
      label: t('demographics.activeMembers'),
      value: t('demographics.peopleValue', { num: String(kpi.totalActiveMembers) }),
      insight: '',
    },
    {
      label: t('demographics.avgTenureMonths'),
      value: t('demographics.monthsValue', { num: String(kpi.avgTenureMonths) }),
      insight:
        kpi.avgTenureMonths >= 6 ? t('demographics.insight.ltv') : t('demographics.insight.churn'),
    },
    { label: t('demographics.avgLtv'), value: `¥${kpi.avgLtv.toLocaleString()}`, insight: '' },
    {
      label: t('demographics.avgMonthlyRevenue'),
      value: `¥${kpi.avgMonthlyRevenue.toLocaleString()}`,
      insight: '',
    },
    {
      label: t('demographics.planChangeRate'),
      value: `${(kpi.planChangeRate * 100).toFixed(1)}%`,
      insight: kpi.planChangeRate > 0.1 ? t('demographics.insight.planOptimize') : '',
    },
    {
      label: t('demographics.nullRateAge'),
      value: `${(kpi.nullRateAge * 100).toFixed(1)}%`,
      insight: kpi.nullRateAge > 0.3 ? t('demographics.insight.dataMissing') : '',
    },
  ];

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
      {cards.map((c) => (
        <div
          key={c.label}
          className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-3"
        >
          <div className="text-[11px] font-medium text-[var(--color-text-secondary)]">
            {c.label}
          </div>
          <div className="mt-1 text-lg font-semibold tabular-nums text-[var(--color-text-primary)]">
            {c.value}
          </div>
          {c.insight ? (
            <div className="mt-1 text-[10px] text-[var(--color-text-muted)]">{c.insight}</div>
          ) : null}
        </div>
      ))}
    </div>
  );
}

/** @graph-connects none */
function ChartCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-4">
      <h2 className="mb-3 text-sm font-semibold text-[var(--color-text-primary)]">{title}</h2>
      {children}
    </div>
  );
}

/** @graph-connects none */
const PLAN_COLORS: Record<string, string> = {
  monthly_monthly: '#6366f1',
  monthly_bimonthly: '#8b5cf6',
  annual_monthly: '#06b6d4',
  annual_bimonthly: '#14b8a6',
};

/** @graph-connects none */
const PLAN_CODE_SHORT_KEYS: Record<string, string> = {
  monthly_monthly: 'demographics.planShort.monthlyMonthly',
  monthly_bimonthly: 'demographics.planShort.monthlyBimonthly',
  annual_monthly: 'demographics.planShort.annualMonthly',
  annual_bimonthly: 'demographics.planShort.annualBimonthly',
};

/** @graph-connects none */
const PLAN_NAME_SHORT_KEYS: Record<string, string> = {
  単月契約かつ1ヶ月に1回交換: 'demographics.planShort.monthlyMonthly',
  単月契約かつ2ヶ月に1回交換: 'demographics.planShort.monthlyBimonthly',
  年間契約かつ1ヶ月に1回交換: 'demographics.planShort.annualMonthly',
  年間契約かつ2ヶ月に1回交換: 'demographics.planShort.annualBimonthly',
};

/** @graph-connects none */
function PlanDistChart({ data }: { data: PlanDistribution[] }) {
  const { t } = useTranslation();
  const total = data.reduce((s, d) => s + d.count, 0);
  return (
    <div className="space-y-2">
      {data.map((d) => {
        const pct = total > 0 ? (d.count / total) * 100 : 0;
        const color = PLAN_COLORS[d.planCode] ?? 'var(--color-accent-primary)';
        return (
          <div key={d.planCode}>
            <div className="flex items-center justify-between text-[12px]">
              <span className="text-[var(--color-text-primary)]">
                {d.planName}
                <span className="ml-1 text-[var(--color-text-muted)]">
                  {t('demographics.pricePerMonth', { price: d.price.toLocaleString() })}
                </span>
              </span>
              <span className="tabular-nums text-[var(--color-text-secondary)]">
                {t('demographics.countWithRatio', {
                  num: String(d.count),
                  ratio: pct.toFixed(1),
                })}
              </span>
            </div>
            <div className="mt-1 h-2 overflow-hidden rounded-full bg-[var(--color-bg-elevated)]">
              <div
                className="h-full rounded-full transition-all"
                style={{ width: `${String(pct)}%`, backgroundColor: color }}
              />
            </div>
          </div>
        );
      })}
      <div className="mt-2 border-t border-[var(--color-border)] pt-2">
        <div className="text-[11px] text-[var(--color-text-muted)]">
          {t('demographics.annualPlanRatio', {
            ratio:
              total > 0
                ? (
                    (data
                      .filter((d) => d.planCode.startsWith('annual'))
                      .reduce((s, d) => s + d.count, 0) /
                      total) *
                    100
                  ).toFixed(1)
                : '0',
          })}{' '}
          — {t('demographics.annualPlanNote')}
        </div>
      </div>
    </div>
  );
}

/** @graph-connects none */
function PlanTrendTable({ data }: { data: PlanTrend[] }) {
  const { t } = useTranslation();
  /** @graph-connects none */
  const months = [...new Set(data.map((d) => d.month))].sort();
  /** @graph-connects none */
  const plans = [...new Set(data.map((d) => d.planCode))];

  if (months.length === 0) {
    return <div className="text-[12px] text-[var(--color-text-muted)]">{t('common.noData')}</div>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[11px]">
        <thead>
          <tr className="border-b border-[var(--color-border)]">
            <th className="py-1.5 pr-2 text-left font-medium text-[var(--color-text-secondary)]">
              {t('demographics.month')}
            </th>
            {plans.map((p) => {
              const planKey = PLAN_CODE_SHORT_KEYS[p];
              return (
                <th
                  key={p}
                  className="px-2 py-1.5 text-right font-medium text-[var(--color-text-secondary)]"
                >
                  {planKey === undefined ? p : t(planKey)}
                </th>
              );
            })}
            <th className="px-2 py-1.5 text-right font-medium text-[var(--color-text-secondary)]">
              {t('demographics.total')}
            </th>
          </tr>
        </thead>
        <tbody>
          {months.map((m) => {
            const rowData = plans.map(
              (p) => data.find((d) => d.month === m && d.planCode === p)?.count ?? 0,
            );
            const total = rowData.reduce((s, c) => s + c, 0);
            return (
              <tr key={m} className="border-b border-[var(--color-border)]">
                <td className="py-1.5 pr-2 tabular-nums text-[var(--color-text-primary)]">{m}</td>
                {rowData.map((c, i) => (
                  <td
                    key={plans[i]}
                    className="px-2 py-1.5 text-right tabular-nums text-[var(--color-text-primary)]"
                  >
                    {String(c)}
                  </td>
                ))}
                <td className="px-2 py-1.5 text-right font-medium tabular-nums text-[var(--color-text-primary)]">
                  {String(total)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** @graph-connects none */
function HorizontalBarChart({ data }: { data: { label: string; count: number; ratio: number }[] }) {
  const { t } = useTranslation();
  const maxCount = Math.max(...data.map((d) => d.count), 1);
  return (
    <div className="space-y-2">
      {data.map((d) => (
        <div key={d.label}>
          <div className="flex items-center justify-between text-[12px]">
            <span className="text-[var(--color-text-primary)]">{d.label}</span>
            <span className="tabular-nums text-[var(--color-text-secondary)]">
              {t('demographics.countWithRatio', {
                num: String(d.count),
                ratio: (d.ratio * 100).toFixed(1),
              })}
            </span>
          </div>
          <div className="mt-1 h-2 overflow-hidden rounded-full bg-[var(--color-bg-elevated)]">
            <div
              className="h-full rounded-full bg-[var(--color-accent-primary)] transition-all"
              style={{ width: `${String((d.count / maxCount) * 100)}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

/** @graph-connects none */
function LtvChart({ data }: { data: LtvBucket[] }) {
  const { t } = useTranslation();
  const maxCount = Math.max(...data.map((d) => d.count), 1);
  return (
    <div className="space-y-2">
      {data.map((d) => (
        <div key={d.label}>
          <div className="flex items-center justify-between text-[12px]">
            <span className="text-[var(--color-text-primary)]">{d.label}</span>
            <span className="tabular-nums text-[var(--color-text-secondary)]">
              {t('demographics.countWithRatioAvg', {
                num: String(d.count),
                ratio: (d.ratio * 100).toFixed(1),
                avg: Math.round(d.avgLtv).toLocaleString(),
              })}
            </span>
          </div>
          <div className="mt-1 h-2 overflow-hidden rounded-full bg-[var(--color-bg-elevated)]">
            <div
              className="h-full rounded-full bg-[#14b8a6] transition-all"
              style={{ width: `${String((d.count / maxCount) * 100)}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

/** @graph-connects none */
function PrefectureChart({ data }: { data: PrefectureDistribution[] }) {
  const { t } = useTranslation();
  const top15 = data.slice(0, 15);
  const maxCount = Math.max(...top15.map((d) => d.count), 1);
  return (
    <div className="space-y-1.5">
      {top15.map((d) => (
        <div key={d.prefecture} className="flex items-center gap-2 text-[12px]">
          <span className="w-16 shrink-0 text-right text-[var(--color-text-primary)]">
            {d.prefecture}
          </span>
          <div className="h-2 flex-1 overflow-hidden rounded-full bg-[var(--color-bg-elevated)]">
            <div
              className="h-full rounded-full bg-[#6366f1] transition-all"
              style={{ width: `${String((d.count / maxCount) * 100)}%` }}
            />
          </div>
          <span className="w-20 shrink-0 tabular-nums text-[var(--color-text-secondary)]">
            {t('demographics.countWithRatio', {
              num: String(d.count),
              ratio: (d.ratio * 100).toFixed(1),
            })}
          </span>
        </div>
      ))}
      {data.length > 15 ? (
        <div className="pt-1 text-[11px] text-[var(--color-text-muted)]">
          {t('demographics.otherPrefectures', { num: String(data.length - 15) })}
        </div>
      ) : null}
    </div>
  );
}

/** @graph-connects none */
function PlanTransitionTable({ data }: { data: PlanTransition[] }) {
  const { t } = useTranslation();

  /** @graph-connects none */
  function shortenPlanName(name: string): string {
    const key = PLAN_NAME_SHORT_KEYS[name];
    return key === undefined ? name : t(key);
  }

  if (data.length === 0) {
    return (
      <div className="text-[12px] text-[var(--color-text-muted)]">
        {t('demographics.noPlanTransition')}
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[12px]">
        <thead>
          <tr className="border-b border-[var(--color-border)]">
            <th className="py-1.5 text-left font-medium text-[var(--color-text-secondary)]">
              {t('demographics.fromPlan')}
            </th>
            <th className="py-1.5 text-center font-medium text-[var(--color-text-secondary)]" />
            <th className="py-1.5 text-left font-medium text-[var(--color-text-secondary)]">
              {t('demographics.toPlan')}
            </th>
            <th className="py-1.5 text-right font-medium text-[var(--color-text-secondary)]">
              {t('demographics.count')}
            </th>
          </tr>
        </thead>
        <tbody>
          {data.map((d) => (
            <tr key={`${d.fromPlan}-${d.toPlan}`} className="border-b border-[var(--color-border)]">
              <td className="py-1.5 text-[var(--color-text-primary)]">
                {shortenPlanName(d.fromPlan)}
              </td>
              <td className="py-1.5 text-center text-[var(--color-text-muted)]">→</td>
              <td className="py-1.5 text-[var(--color-text-primary)]">
                {shortenPlanName(d.toPlan)}
              </td>
              <td className="py-1.5 text-right tabular-nums text-[var(--color-text-primary)]">
                {String(d.count)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** @graph-connects none */
function formatJst(isoDate: string): string {
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return isoDate;
  return d.toLocaleString('ja-JP', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}
